import { ManipulatedVariable, CorrelationRule } from '../types';

export const CORRELATION_RULES: CorrelationRule[] = [
  {
    id: 'rule-hot-cold-feed',
    name: 'Hot Feed & Cold Feed Coupled Testing',
    tagsA: ['TC-102', 'TC-225'],
    tagsB: ['FC-101', 'FC-201', 'FC-301'],
    description: 'Hot feed and cold feed should NOT be stepped simultaneously. Thermal dynamics decouple column hydraulic inventory.',
    cautionLevel: 'warning',
  },
  {
    id: 'rule-frac-dist-pairs',
    name: 'Frac Distribution Pairs Simultaneous Testing',
    tagsA: ['RC-202'],
    tagsB: ['RC-302'],
    description: 'Frac distribution pairs (Frac 1 RC-202 vs Frac 2 RC-302) are directly coupled. Simultaneous steps will corrupt FIR impulse response models.',
    cautionLevel: 'alert',
  },
  {
    id: 'rule-reflux-pressure',
    name: 'Reflux & Column Pressure Stepped Together',
    tagsA: ['FC-104', 'FC-204', 'FC-304'],
    tagsB: ['PC-105', 'PC-205', 'PC-305'],
    description: 'Stepping Reflux Flow and Overhead Column Pressure simultaneously obscures tray vapor-liquid equilibrium (VLE) separation gains.',
    cautionLevel: 'warning',
  },
  {
    id: 'rule-reflux-condenser-temp',
    name: 'Reflux & Condenser Temperature Together',
    tagsA: ['FC-104', 'FC-204', 'FC-304'],
    tagsB: ['TC-113', 'TC-214', 'TC-314'],
    description: 'Reflux rate and condenser temperature directly mask subcooling effects. Model identification requires independent excitation.',
    cautionLevel: 'warning',
  },
  {
    id: 'rule-bottoms-hot-oil',
    name: 'Bottoms Temp & Hot Oil Temperature Together',
    tagsA: ['TC-115', 'TC-216', 'TC-316', 'FC-107', 'FC-207', 'FC-307'],
    tagsB: ['TC-325'],
    description: 'Stepping column stripping/bottom temperature setpoints alongside Hot Oil header temperature induces multi-collinearity in reboiler heat transfer curves.',
    cautionLevel: 'alert',
  },
];

export interface CorrelationConflict {
  rule: CorrelationRule;
  conflictingMv: ManipulatedVariable;
  message: string;
}

export function checkCorrelationConflicts(
  targetMvTag: string,
  allMvs: ManipulatedVariable[]
): CorrelationConflict[] {
  const conflicts: CorrelationConflict[] = [];

  // Find other MVs that are currently 'Active' or 'Waiting for Steady State'
  const activeMvs = allMvs.filter(
    (m) => (m.status === 'Active' || m.status === 'Waiting for Steady State') && m.tag !== targetMvTag
  );

  for (const rule of CORRELATION_RULES) {
    const isTagInA = rule.tagsA.includes(targetMvTag);
    const isTagInB = rule.tagsB.includes(targetMvTag);

    if (!isTagInA && !isTagInB) continue;

    const opposingTags = isTagInA ? rule.tagsB : rule.tagsA;

    for (const activeMv of activeMvs) {
      if (opposingTags.includes(activeMv.tag)) {
        conflicts.push({
          rule,
          conflictingMv: activeMv,
          message: `Active test on ${activeMv.tag} (${activeMv.description}) conflicts with selected ${targetMvTag}. ${rule.description}`,
        });
      }
    }
  }

  return conflicts;
}

export function getGeneralCorrelationNotice(targetMvTag: string): string[] {
  const notices: string[] = [];
  for (const rule of CORRELATION_RULES) {
    if (rule.tagsA.includes(targetMvTag) || rule.tagsB.includes(targetMvTag)) {
      notices.push(`${rule.name}: ${rule.description}`);
    }
  }
  return notices;
}
